import React, { useMemo, useState } from 'react';
import {
  useEngagementGroups,
  newGroupId,
  GROUP_COLORS,
  type EngagementGroup,
} from '../hooks/useEngagementGroups';

/**
 * Étape 3 — Groupes d'engagement.
 * Le joueur compose ses groupes (nom, couleur, composition par type d'engin) et voit
 * immédiatement s'ils sont **honorables maintenant** : un engin ne compte que s'il est
 * disponible **et armé** (cf. moniteur d'armement). Réglage local au poste du joueur.
 */

interface Props {
  /** Types d'engins réellement présents dans le parc du joueur. */
  fleetTypes: string[];
  /** type d'engin → nombre d'engins disponibles ET armés. */
  availableByType: Record<string, number>;
}

const EngagementGroupsPanel: React.FC<Props> = ({ fleetTypes, availableByType }) => {
  const { groups, saveGroup, deleteGroup } = useEngagementGroups();
  const [editing, setEditing] = useState<EngagementGroup | null>(null);

  const startNew = () =>
    setEditing({ id: newGroupId(), nom: '', couleur: GROUP_COLORS[0], compo: {} });

  const feasibility = useMemo(
    () => (g: EngagementGroup) => {
      const manques: string[] = [];
      for (const [type, need] of Object.entries(g.compo)) {
        const have = Number(availableByType[type] || 0);
        if (have < need) manques.push(`${type} ${have}/${need}`);
      }
      return manques;
    },
    [availableByType],
  );

  return (
    <div className="rounded-lg border border-slate-700/70 bg-slate-950/50 p-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Groupes d'engagement
        </h3>
        {!editing ? (
          <button
            type="button"
            onClick={startNew}
            className="rounded border border-slate-600/50 bg-slate-800/50 px-2 py-0.5 text-[10px] font-semibold text-slate-200 hover:bg-slate-700/70"
          >
            ＋ Nouveau
          </button>
        ) : null}
      </div>

      {editing ? (
        <GroupEditor
          value={editing}
          fleetTypes={fleetTypes}
          onCancel={() => setEditing(null)}
          onSave={(g) => {
            saveGroup(g);
            setEditing(null);
          }}
        />
      ) : groups.length === 0 ? (
        <p className="py-2 text-center text-[10px] text-slate-500">
          Aucun groupe. Composez-en un pour engager plusieurs engins d'un coup.
        </p>
      ) : (
        <ul className="space-y-1">
          {groups.map((g) => {
            const manques = feasibility(g);
            const compoTxt = Object.entries(g.compo)
              .map(([t, n]) => `${t} ×${n}`)
              .join(' · ');
            return (
              <li
                key={g.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-700/60 bg-slate-900/60 px-2 py-1"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-3 w-3 shrink-0 rounded-sm border border-black/30"
                    style={{ backgroundColor: g.couleur }}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-semibold text-slate-100">{g.nom}</p>
                    <p className="truncate text-[9px] text-slate-500">{compoTxt || 'Composition vide'}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {manques.length === 0 ? (
                    <span className="rounded border border-emerald-500/45 bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-200">
                      Armable
                    </span>
                  ) : (
                    <span
                      className="rounded border border-amber-500/45 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-amber-200"
                      title={`Manque : ${manques.join(', ')}`}
                    >
                      Manque {manques.join(', ')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing(g)}
                    title="Modifier"
                    className="rounded border border-slate-600/50 bg-slate-800/50 px-1.5 py-0.5 text-[9px] text-slate-300 hover:bg-slate-700/70"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteGroup(g.id)}
                    title="Supprimer"
                    className="rounded border border-red-500/40 bg-red-900/30 px-1.5 py-0.5 text-[9px] text-red-300 hover:bg-red-800/50"
                  >
                    ✕
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const GroupEditor: React.FC<{
  value: EngagementGroup;
  fleetTypes: string[];
  onCancel: () => void;
  onSave: (g: EngagementGroup) => void;
}> = ({ value, fleetTypes, onCancel, onSave }) => {
  const [draft, setDraft] = useState<EngagementGroup>(value);

  const setCount = (type: string, n: number) =>
    setDraft((d) => {
      const compo = { ...d.compo };
      if (n <= 0) delete compo[type];
      else compo[type] = n;
      return { ...d, compo };
    });

  const total = Object.values(draft.compo).reduce((a, b) => a + b, 0);
  const canSave = draft.nom.trim().length > 0 && total > 0;

  return (
    <div className="space-y-2">
      <input
        value={draft.nom}
        onChange={(e) => setDraft((d) => ({ ...d, nom: e.target.value }))}
        placeholder="Nom du groupe (ex. Incendie habitation)"
        className="w-full rounded border border-slate-600/60 bg-slate-900/80 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
      />

      <div className="flex items-center gap-1">
        <span className="text-[9px] text-slate-500">Couleur :</span>
        {GROUP_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setDraft((d) => ({ ...d, couleur: c }))}
            className={`h-4 w-4 rounded-sm border transition-transform ${
              draft.couleur === c ? 'scale-110 border-white' : 'border-black/30'
            }`}
            style={{ backgroundColor: c }}
            aria-label={`Couleur ${c}`}
          />
        ))}
      </div>

      <div className="space-y-1">
        {fleetTypes.length === 0 ? (
          <p className="text-[10px] text-slate-500">Aucun type d'engin dans le parc.</p>
        ) : (
          fleetTypes.map((type) => {
            const n = Number(draft.compo[type] || 0);
            return (
              <div key={type} className="flex items-center justify-between gap-2">
                <span className="truncate text-[10px] text-slate-300">{type}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCount(type, n - 1)}
                    className="rounded border border-slate-600/50 bg-slate-800/60 px-1.5 text-[10px] text-slate-200 hover:bg-slate-700/70"
                  >
                    −
                  </button>
                  <span className="w-5 text-center text-[10px] font-semibold text-slate-100">{n}</span>
                  <button
                    type="button"
                    onClick={() => setCount(type, n + 1)}
                    className="rounded border border-slate-600/50 bg-slate-800/60 px-1.5 text-[10px] text-slate-200 hover:bg-slate-700/70"
                  >
                    ＋
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave({ ...draft, nom: draft.nom.trim() })}
          className="rounded bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          Enregistrer
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded bg-zinc-700 px-2 py-1 text-[10px] text-white hover:bg-zinc-600"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

export default EngagementGroupsPanel;
